/**
 * Generating phase — the booth assembling itself.
 *
 * The generation call takes 10–25s. That is the best attention window /new
 * gets, and it used to be spent on a grey rectangle with a wand in it — the
 * least photographic thing we could have shown on a photobooth product.
 *
 * Now the same device frame from the hero stays on screen and fills in: the
 * backdrop washes in, the palette lands, the headline and subtext rule
 * themselves in, the CTA pops. Stages are still timed locally (the API is one
 * await, so there is no real progress to report) and the last one lingers
 * until the response arrives — but every stage now names actual work, and the
 * skeleton is shaped like the thing being built.
 *
 * Colour comes from the vibe chip the visitor picked, when they picked one;
 * otherwise it uses the brand accent. Either way the shape is honest: this is
 * a welcome screen being laid out, not a progress bar pretending.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { useOnboardingStore } from '../onboardingStore'
import { THEME_TEMPLATES } from '../themeTemplates'
import { useCopy, type Copy } from '../regionCopy'

interface Stage {
  label: string
  /** How much of the skeleton exists by the time this stage is running. */
  reveal: 'backdrop' | 'palette' | 'headline' | 'body' | 'cta' | 'done'
}

const stages = (c: Copy): Stage[] => [
  { label: c.stageReading, reveal: 'backdrop' },
  { label: c.stagePalette, reveal: 'palette' },
  { label: c.stageLayout, reveal: 'headline' },
  { label: c.stagePainting, reveal: 'body' },
  { label: c.stagePicking, reveal: 'cta' },
  { label: c.stageChecking, reveal: 'done' }
]

const STAGE_MS = 3200
const ORDER: Stage['reveal'][] = ['backdrop', 'palette', 'headline', 'body', 'cta', 'done']

/**
 * The palette the visitor steered towards, read back out of the prompt suffix
 * that the vibe chips append. Falls back to the brand accent.
 */
function useSteeredPalette(prompt: string): { accent: string; ground: string; ink: string } {
  return useMemo(() => {
    const match = /Palette direction: ([^—]+)—/.exec(prompt)
    const name = match?.[1]?.trim().toLowerCase()
    const t = name ? THEME_TEMPLATES.find((x) => x.name.toLowerCase() === name) : undefined
    if (!t) return { accent: '#007AFF', ground: '#17171C', ink: '#FBFBFB' }
    return {
      accent: t.primaryColor,
      ground: t.backgroundColor,
      ink: t.dark ? '#FBFBFB' : '#1D1D1D'
    }
  }, [prompt])
}

export const GenerationTheater: React.FC = () => {
  const prompt = useOnboardingStore((s) => s.prompt)
  const c = useCopy()
  const STAGES = stages(c)
  const [stage, setStage] = useState(0)
  const [slow, setSlow] = useState(false)
  const palette = useSteeredPalette(prompt)

  useEffect(() => {
    const t = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), STAGE_MS)
    // The stages run out well before a slow generation does, leaving the last
    // one on screen indefinitely — which reads as frozen. Say so instead.
    const slowTimer = setTimeout(() => setSlow(true), STAGE_MS * (STAGES.length + 2))
    return () => {
      clearInterval(t)
      clearTimeout(slowTimer)
    }
  }, [])

  const reached = (r: Stage['reveal']): boolean =>
    ORDER.indexOf(r) <= ORDER.indexOf(STAGES[stage].reveal)

  /** The prompt minus the vibe-chip suffix — that part isn't the wish. */
  const spoken = prompt.replace(/\n\nPalette direction:.*$/s, '').trim()

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-7 px-6 py-12 text-center">
      <div
        className="relative w-[13.5rem] overflow-hidden rounded-[1.75rem] shadow-2xl ring-1 ring-white/15 transition-colors duration-1000"
        style={{
          aspectRatio: '540 / 960',
          backgroundColor: reached('backdrop') ? palette.ground : '#141418'
        }}
      >
        {/* Backdrop wash */}
        <div
          aria-hidden
          className="absolute inset-0 transition-opacity duration-1000"
          style={{
            opacity: reached('body') ? 0.85 : 0,
            background: `radial-gradient(120% 80% at 50% 8%, ${palette.accent}55 0%, transparent 70%)`
          }}
        />

        {/* Welcome screen skeleton, laid out where the real one goes */}
        <div className="absolute inset-x-0 top-[26%] flex flex-col items-center gap-3 px-6">
          <div
            className="h-4 w-4/5 rounded transition-all duration-700"
            style={{
              backgroundColor: palette.ink,
              opacity: reached('headline') ? 0.9 : 0,
              transform: reached('headline') ? 'none' : 'translateY(8px)'
            }}
          />
          <div
            className="h-2.5 w-3/5 rounded transition-all duration-700 delay-100"
            style={{
              backgroundColor: palette.ink,
              opacity: reached('body') ? 0.45 : 0,
              transform: reached('body') ? 'none' : 'translateY(8px)'
            }}
          />
        </div>

        <div className="absolute inset-x-0 bottom-[16%] flex justify-center">
          <div
            className="h-7 w-28 rounded-full transition-all duration-500"
            style={{
              backgroundColor: palette.accent,
              opacity: reached('cta') ? 1 : 0,
              transform: reached('cta') ? 'scale(1)' : 'scale(0.85)'
            }}
          />
        </div>

        {/* Palette landing in the corner */}
        <div className="absolute left-3 top-3 flex gap-1.5">
          {[palette.ground, palette.accent, palette.ink].map((c, i) => (
            <span
              key={i}
              className="h-2.5 w-2.5 rounded-full ring-1 ring-black/20 transition-all duration-500"
              style={{
                backgroundColor: c,
                opacity: reached('palette') ? 1 : 0,
                transform: reached('palette') ? 'none' : 'translateY(-6px)',
                transitionDelay: `${i * 90}ms`
              }}
            />
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-lg font-semibold text-whiteout">{STAGES[stage].label}</p>
        {slow && <p className="mt-1 text-xs text-whiteout/50">{c.theaterSlow}</p>}
        {spoken && <p className="mx-auto max-w-md text-sm italic text-whiteout/40">“{spoken}”</p>}
      </div>

      <div className="flex items-center gap-2">
        {STAGES.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i <= stage ? 'w-6 bg-blueSparkle' : 'w-1.5 bg-white/15'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

export default GenerationTheater
